import { LeaseSignatureInfo } from 'api/models';
import classNames from 'classnames/bind';
import Button from 'components/Button/Button';
import { palette } from 'styles/palette';
import ProfileTile, {
  PROFILE_TILE_SPACING,
} from '../../components/ProfileTile/ProfileTile';
import styles from './ResidentTileWithStatus.module.css';

const cx = classNames.bind(styles);

/**
 * The per-tile props `ResidentListContainer` builds and this component forwards
 * to `ProfileTile`. Only `subtitles` is required: it is the one field all three
 * sources that feed the list — residents, kids and pets — always fill, and the
 * `includes` checks below read it without guarding.
 */
export type ResidentProfileProps = {
  subtitles: string[];
  imageUrl?: string;
  title?: string;
  userId?: string;
  signatureStatus?: string;
  customEmptyProfileIcon?: string;
};

const statusColorMap = (
  status: string | undefined,
  externalSignedPrimaryResident: boolean | undefined,
) => {
  if (externalSignedPrimaryResident) {
    return palette['dark-green'];
  }

  switch (status) {
    case 'Pending':
      return palette.red;
    case 'Signed':
      return palette.green;
    default:
      return palette['dark-gray'];
  }
};

const isExternalSignedPrimaryResident = (
  signatureInformation: LeaseSignatureInfo | undefined,
  profileProps: ResidentProfileProps,
) => {
  if (
    signatureInformation?.signMode === 'External' &&
    signatureInformation?.hasResidentCompletedSigningFlow &&
    profileProps.subtitles.includes('Primary Resident')
  ) {
    return true;
  }
};

const isExternalNonPrimaryResident = (
  signatureInformation: LeaseSignatureInfo | undefined,
  profileProps: ResidentProfileProps,
) => {
  if (
    signatureInformation?.signMode === 'External' &&
    !profileProps.subtitles.includes('Primary Resident')
  ) {
    return true;
  }
  return false;
};

const displayStatusMap = (
  status: string | undefined,
  externalSignedPrimaryResident: boolean | undefined,
  externalNonPrimaryResident: boolean,
) => {
  if (externalSignedPrimaryResident) {
    return 'Signed';
  }

  if (externalNonPrimaryResident) {
    return '';
  }

  switch (status) {
    case 'Pending':
      return 'Signature Pending';
    default:
      return status;
  }
};

type Props = {
  profileProps: ResidentProfileProps;
  ctaLabel?: string;
  onCTAClick?: () => void;
  residentInfo?: Record<string, unknown>[];
  // `null` rather than `undefined`, because that is the default the parameter
  // below already used and the default `ProfileTile` itself declares.
  alignProfileItems?: string | null;
  // Optional because only the tours-and-apps card supplies it; every read of it
  // here already goes through `?.`.
  signatureInformation?: LeaseSignatureInfo;
};

const ResidentTileWithStatus = ({
  profileProps,
  ctaLabel = '',
  onCTAClick = () => {},
  alignProfileItems = null,
  residentInfo = [],
  signatureInformation,
}: Props) => {
  const externalSignedPrimaryResident = isExternalSignedPrimaryResident(
    signatureInformation,
    profileProps,
  );
  const externalNonPrimaryResident = isExternalNonPrimaryResident(
    signatureInformation,
    profileProps,
  );

  return (
    <div className={cx('renter')}>
      <div className={cx('tile-with-status-container')}>
        <div className={cx('tile-container')}>
          <ProfileTile
            relative
            alignProfileItems={alignProfileItems}
            spacing={PROFILE_TILE_SPACING.NO_SPACING}
            text={residentInfo}
            {...profileProps}
          />
        </div>
        <div
          className={cx('signature-status-container')}
          style={{
            color: statusColorMap(
              profileProps.signatureStatus,
              externalSignedPrimaryResident,
            ),
          }}
        >
          {displayStatusMap(
            profileProps.signatureStatus,
            externalSignedPrimaryResident,
            externalNonPrimaryResident,
          )}
        </div>
      </div>

      {ctaLabel && <Button label={ctaLabel} onClick={onCTAClick} />}
    </div>
  );
};

ResidentTileWithStatus.MODES = ProfileTile.MODES;

export default ResidentTileWithStatus;
